import {useSnackBar} from "@hooks/alert/useSnackBar";
import {buildErrorsMessages} from "@ui/errors/helpers/helpers";
import {useMutation, useQuery} from "@tanstack/react-query";
import {useTranslation} from "react-i18next";
import {useShowError} from "@hooks/useShowError/useShowError";

import {QUERY_KEY_TASKS_SINGLE_VOYAGES} from "@containers/singleVoyage/tasks/queryHooks/constants/queryKey";
import {
    changeExecutionStatusSingleVoyage,
    closeTaskSingleVoyage,
    deleteTaskSingleVoyage,
    getTasksSingleVoyage,
    saveTaskSingleVoyage,
    saveTasksSingleVoyage
} from "@containers/singleVoyage/tasks/api/api";
import {BodySaveTasksSingleVoyage, IPropsGetTasksSingleVoyage} from "@containers/singleVoyage/tasks/types";
import {refetchTasksSingleVoyage} from "@containers/singleVoyage/tasks/helpers/refetchTasks";


export const useSaveTaskSingleVoyageQuery = ({
                                                 onSuccess
                                             }: {
    onSuccess?: () => void
} = {}) => {
    const {setSnackBar} = useSnackBar()

    const {t} = useTranslation()

    return useMutation({
        mutationFn: saveTaskSingleVoyage,
        onSuccess: () => {
            refetchTasksSingleVoyage()
            setSnackBar({
                title: t('base.successSaveMessage'),
                variant: 'info'
            })
            onSuccess && onSuccess()
        },
        onError: (e) => {
            const message = buildErrorsMessages(e)
            setSnackBar({
                message,
                variant: 'error'
            })
        }
    });
}

export const useSaveTasksSingleVoyageQuery = ({
                                                  onSuccess,
                                                  onError,
                                                  disableRefetch,
                                              }: {
    onSuccess?: () => void,
    onError?: (error: any) => void,
    disableRefetch?: boolean
} = {}) => {
    const {setSnackBar} = useSnackBar()

    const {t} = useTranslation()

    return useMutation({
        mutationFn: (body: BodySaveTasksSingleVoyage) => saveTasksSingleVoyage(body, onError),
        onSuccess: () => {
            if (!disableRefetch) {
                refetchTasksSingleVoyage()
            }
            setSnackBar({
                title: t('base.successSaveMessage'),
                variant: 'info'
            })
            onSuccess && onSuccess()
        },
        onError: (e) => {
            const message = buildErrorsMessages(e)
            setSnackBar({
                message,
                variant: 'error'
            })
        }
    });
}


export const useDeleteTaskSingleVoyageQuery = ({
                                                   onSuccess,
                                                   hasSnackbar,
                                                   hasRefetch,
                                               }: {
    onSuccess?: () => void,
    hasSnackbar?: boolean
    hasRefetch?: boolean
} = {
    hasSnackbar: true,
    hasRefetch: true,
}) => {
    const {setSnackBar} = useSnackBar()

    const {t} = useTranslation()

    return useMutation({
        mutationFn: deleteTaskSingleVoyage,
        onSuccess: () => {
            if (hasRefetch !== false) {
                refetchTasksSingleVoyage()
            }
            if (hasSnackbar !== false) {
                setSnackBar({
                    title: t('successDeleteMessage'),
                    variant: 'info'
                })
            }
            onSuccess && onSuccess()
        },
        onError: (e) => {
            const message = buildErrorsMessages(e)
            setSnackBar({
                message,
                variant: 'error'
            })
        }
    });
}

export const useGetTasksSingleVoyageQuery = ({
                                                 params,
                                                 enabled,
                                                 refetchInterval
                                             }: {
    params: IPropsGetTasksSingleVoyage,
    enabled?: boolean,
    refetchInterval?: number
}) => {
    const {onShowError} = useShowError()
    return useQuery({
        queryFn: () => getTasksSingleVoyage(params, onShowError),
        queryKey: [QUERY_KEY_TASKS_SINGLE_VOYAGES, params?.accountId, params?.voyageId],
        staleTime: 1000 * 60 * 30,
        enabled: (typeof enabled !== 'undefined' ? enabled : true) && !!params?.voyageId && !!params?.accountId,
        refetchInterval
    });
}


/** Closing the work at a single stop. */
export const useChangeExecutionStatusSingleVoyageQuery = ({
                                                              onSuccess
                                                          }: {
    onSuccess?: () => void
} = {}) => {
    const {setSnackBar} = useSnackBar()

    const {t} = useTranslation()

    return useMutation({
        mutationFn: changeExecutionStatusSingleVoyage,
        onSuccess: () => {
            refetchTasksSingleVoyage()
            setSnackBar({
                title: t('base.successSaveMessage'),
                variant: 'info'
            })
            onSuccess && onSuccess()
        },
        onError: (e) => {
            const message = buildErrorsMessages(e)
            setSnackBar({
                message,
                variant: 'error'
            })
        }
    });
}

/** Closing a task in one action: only finishes off executions that are still open. */
export const useCloseTaskSingleVoyageQuery = ({
                                                  onSuccess
                                              }: {
    onSuccess?: () => void
} = {}) => {
    const {setSnackBar} = useSnackBar()

    const {t} = useTranslation()

    return useMutation({
        mutationFn: closeTaskSingleVoyage,
        onSuccess: () => {
            refetchTasksSingleVoyage()
            setSnackBar({
                title: t('base.successSaveMessage'),
                variant: 'info'
            })
            onSuccess && onSuccess()
        },
        onError: (e) => {
            const message = buildErrorsMessages(e)
            setSnackBar({
                message,
                variant: 'error'
            })
        }
    });
}
